import { useState, useEffect, useRef } from 'react'
import {
  Calendar,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Clock3,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import {
  useGetCollaborationEventsQuery,
  useRsvpCollaborationEventMutation,
  type CollaborationEvent,
} from '@/redux/collaborationsApi'

interface CollaborationEventsTabProps {
  campaignId: string
  highlightEventId?: string
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function RsvpBadge({ status }: { status: CollaborationEvent['rsvpStatus'] }) {
  const map = {
    pending: {
      label: 'Awaiting your response',
      color: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    },
    accepted: {
      label: 'You accepted',
      color: 'text-[#C0FD72] bg-[#C0FD72]/10 border-[#C0FD72]/20',
    },
    declined: { label: 'You declined', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
    proposed_changes: {
      label: 'You proposed a new time',
      color: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    },
  }
  const { label, color } = map[status] ?? map.pending
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${color}`}>{label}</span>
  )
}

const TIME_OPTIONS = (() => {
  const opts: { value: string; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const period = h < 12 ? 'AM' : 'PM'
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
      opts.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` })
    }
  }
  return opts
})()

function buildIso(date: string, time: string): string | null {
  if (!date || !time) return null
  return new Date(`${date}T${time}:00`).toISOString()
}

function addHour(time: string): string {
  const [h, m] = time.split(':').map(Number)
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function TimePicker({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selected = TIME_OPTIONS.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  useEffect(() => {
    if (open && listRef.current && value) {
      const idx = TIME_OPTIONS.findIndex((o) => o.value === value)
      if (idx >= 0) {
        const item = listRef.current.children[idx] as HTMLElement | undefined
        item?.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [open, value])

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-1 border border-white/12 rounded-lg px-2 py-2 text-xs bg-white/6 text-white focus:outline-hidden focus:border-[#C0FD72]/40 cursor-pointer"
      >
        <span className={selected ? 'text-white' : 'text-gray-500'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-500 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/10 bg-[#1a2233] shadow-xl overflow-hidden">
          <div ref={listRef} className="max-h-48 overflow-y-auto overscroll-contain">
            {TIME_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value)
                  setOpen(false)
                }}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                  o.value === value
                    ? 'bg-[#C0FD72]/15 text-[#C0FD72]'
                    : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ProposeTimeForm({
  eventId,
  campaignId,
  onDone,
}: {
  eventId: string
  campaignId: string
  onDone: () => void
}) {
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [note, setNote] = useState('')
  const [rsvp, { isLoading }] = useRsvpCollaborationEventMutation()

  const handleStartTimeChange = (v: string) => {
    setStartTime(v)
    if (!endTime) setEndTime(addHour(v))
  }

  const startIso = buildIso(date, startTime)
  const endIso = buildIso(date, endTime)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!startIso) return
    try {
      await rsvp({
        campaignId,
        eventId,
        rsvpStatus: 'proposed_changes',
        athleteNote: note.trim() || undefined,
        proposedStartAt: startIso,
        proposedEndAt: endIso ?? undefined,
      }).unwrap()
      toast('New time proposed', { description: 'Your manager will be notified.' })
      onDone()
    } catch {
      toast.error('Failed to propose new time')
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
    >
      <p className="text-white text-xs font-semibold">Propose a new time</p>

      <div className="space-y-2">
        <label className="text-gray-500 text-xs block">New date & time *</label>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ colorScheme: 'dark' }}
            className="flex-1 min-w-[130px] bg-white/8 border border-white/12 rounded-lg px-3 py-2 text-white text-xs focus:outline-hidden focus:border-[#C0FD72]/40"
          />
          <TimePicker value={startTime} onChange={handleStartTimeChange} placeholder="Start" />
          <span className="text-gray-600 text-xs shrink-0">–</span>
          <TimePicker value={endTime} onChange={setEndTime} placeholder="End" />
        </div>
      </div>

      <div>
        <label className="text-gray-500 text-xs block mb-1">Note (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. I have practice until 5pm that day"
          rows={2}
          style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'white' }}
          className="w-full border border-white/12 rounded-lg px-3 py-2 text-xs placeholder:text-gray-500 resize-none focus:outline-hidden focus:border-[#C0FD72]/40"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onDone}
          className="flex-1 py-2 text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded-xl transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !startIso}
          className="flex-1 py-2 text-xs font-semibold bg-[#C0FD72] text-black hover:bg-[#b0ef62] rounded-xl transition-colors disabled:opacity-40"
        >
          {isLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
          ) : (
            'Submit proposal'
          )}
        </button>
      </div>
    </form>
  )
}

function EventCard({
  event,
  campaignId,
  highlighted,
  cardRef,
}: {
  event: CollaborationEvent
  campaignId: string
  highlighted?: boolean
  cardRef?: React.RefObject<HTMLDivElement>
}) {
  const [showPropose, setShowPropose] = useState(false)
  const [rsvp, { isLoading }] = useRsvpCollaborationEventMutation()

  const isCancelled = event.status === 'cancelled'
  const isPending = event.rsvpStatus === 'pending'
  const hasProposed = event.rsvpStatus === 'proposed_changes'

  const handleRsvp = async (status: 'accepted' | 'declined') => {
    try {
      await rsvp({ campaignId, eventId: event.id, rsvpStatus: status }).unwrap()
      toast(status === 'accepted' ? 'Event accepted' : 'Event declined', {
        description: 'Your manager has been notified.',
      })
    } catch {
      toast.error('Failed to update response')
    }
  }

  return (
    <div
      ref={cardRef}
      className={`rounded-2xl border p-4 space-y-3 transition-all ${
        highlighted
          ? 'ring-2 ring-[#C0FD72]/40 border-[#C0FD72]/30 bg-white/5'
          : isCancelled
            ? 'bg-white/3 border-white/8 opacity-50'
            : isPending
              ? 'bg-white/5 border-amber-400/20'
              : 'bg-white/5 border-white/8'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-white/8 border border-white/12 flex items-center justify-center shrink-0">
          <Calendar className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p
              className={`text-sm font-semibold ${isCancelled ? 'line-through text-gray-500' : 'text-white'}`}
            >
              {event.title}
            </p>
            {isCancelled ? (
              <span className="text-xs text-gray-600 bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">
                Cancelled
              </span>
            ) : (
              <RsvpBadge status={event.rsvpStatus} />
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span>{formatDateTime(event.startAt)}</span>
              {event.endAt && (
                <span className="text-gray-600">– {formatDateTime(event.endAt)}</span>
              )}
            </div>
            {event.location && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span>{event.location}</span>
              </div>
            )}
          </div>

          {event.description && (
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">{event.description}</p>
          )}
        </div>
      </div>

      {/* Own proposal summary */}
      {hasProposed && event.proposedStartAt && (
        <div className="bg-blue-400/8 border border-blue-400/15 rounded-xl p-3">
          <p className="text-blue-300 text-xs font-medium mb-1">Your proposed time</p>
          <p className="text-blue-200/80 text-xs">
            {formatDateTime(event.proposedStartAt)}
            {event.proposedEndAt && ` – ${formatDateTime(event.proposedEndAt)}`}
          </p>
          {event.athleteNote && (
            <p className="text-blue-100/60 text-xs italic mt-1">"{event.athleteNote}"</p>
          )}
        </div>
      )}

      {/* Action buttons for pending events */}
      {!isCancelled && isPending && !showPropose && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => handleRsvp('accepted')}
            disabled={isLoading}
            className="flex-1 py-2.5 text-sm font-semibold border border-[#C0FD72]/50 text-[#C0FD72] hover:bg-[#C0FD72]/10 rounded-xl transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Accept
              </>
            )}
          </button>
          <button
            onClick={() => setShowPropose(true)}
            disabled={isLoading}
            className="flex-1 py-2.5 text-sm text-gray-300 hover:text-white border border-white/10 hover:border-white/20 rounded-xl transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            <Clock3 className="w-4 h-4" />
            New time
          </button>
          <button
            onClick={() => handleRsvp('declined')}
            disabled={isLoading}
            className="flex-1 py-2.5 text-sm text-red-400 hover:text-red-300 border border-red-400/20 hover:border-red-400/40 rounded-xl transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            <XCircle className="w-4 h-4" />
            Decline
          </button>
        </div>
      )}

      {!isCancelled && isPending && showPropose && (
        <ProposeTimeForm
          eventId={event.id}
          campaignId={campaignId}
          onDone={() => setShowPropose(false)}
        />
      )}
    </div>
  )
}

const CollaborationEventsTab = ({ campaignId, highlightEventId }: CollaborationEventsTabProps) => {
  const { data: events = [], isLoading } = useGetCollaborationEventsQuery(campaignId)
  const highlightRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isLoading && highlightEventId && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 200)
    }
  }, [isLoading, highlightEventId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  const pending = events.filter((e) => e.rsvpStatus === 'pending' && e.status === 'scheduled')
  const others = events.filter((e) => e.rsvpStatus !== 'pending' || e.status === 'cancelled')

  return (
    <div className="space-y-4">
      {events.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-white/3 p-8 text-center">
          <Calendar className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm font-medium">No events scheduled</p>
          <p className="text-gray-600 text-xs mt-1">
            Your manager will schedule events for this collaboration here.
          </p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                Needs your response
              </p>
              <div className="space-y-3">
                {pending.map((event) => {
                  const isHighlighted = event.id === highlightEventId
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      campaignId={campaignId}
                      highlighted={isHighlighted}
                      cardRef={isHighlighted ? highlightRef : undefined}
                    />
                  )
                })}
              </div>
            </div>
          )}
          {others.length > 0 && (
            <div>
              {pending.length > 0 && (
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 mt-5">
                  Upcoming &amp; Past
                </p>
              )}
              <div className="space-y-3">
                {others.map((event) => {
                  const isHighlighted = event.id === highlightEventId
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      campaignId={campaignId}
                      highlighted={isHighlighted}
                      cardRef={isHighlighted ? highlightRef : undefined}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CollaborationEventsTab
